// Rider numbers (participant ids): each person's answers on a booking are
// bound to ONE permanent id that only the server issues, kept inside that
// person's row as `_pid` (~/vertu-design/2026-09-25-rider-ids-spec.md, and
// the guest-profile spec's row markers).
//
// This module is the client half: slice P2 carried the ids, and P3a
// (20261001110020) makes the server issue them. Every stored rider row now
// carries its `_pid`; every writer matches rows by it and never by place
// (a row without one is a new rider; a stored id left out is refused; a
// saved rider named by another's id is refused). A client only CARRIES an
// id it was given (a reader's rows, a whole-list save's participant_ids, a
// one-rider save's participant_id) and never makes one up. With no id from
// the server (a server before P3a), nothing here changes what is sent.
//
// Pure: no React, no network, no storage. e2e/participant-ids-logic.spec.ts
// imports it directly.

import type { GuestNoun } from "./booking-questions";

// ─── the reserved row keys ──────────────────────────────────────────────────
/** A person's id, on a kept row (stored). */
export const PARTICIPANT_ID_KEY = "_pid";
/** A tombstone: exactly {_pid, _remove: true}. Never stored. */
export const REMOVE_KEY = "_remove";
/** "Yes, still the same person, fixing a spelling", on a kept row. Never stored. */
export const SAME_PERSON_KEY = "_same_person";
/** "This person is me", on a new row only (guest profiles). Never stored. */
export const SELF_KEY = "_self";
/** A person picked from "people you've booked for before", on a new row only. Never stored. */
export const PICK_KEY = "_pick";
/**
 * Every key a saved row may carry beside the answers. A question's key can
 * never start with "_" (^[a-z][a-z0-9_]{0,39}$), so these never collide
 * with an answer. The server refuses any other "_" key.
 */
export const RESERVED_ROW_KEYS = [PARTICIPANT_ID_KEY, REMOVE_KEY, SAME_PERSON_KEY, SELF_KEY, PICK_KEY] as const;
export type ReservedRowKey = (typeof RESERVED_ROW_KEYS)[number];

/**
 * A draft row's own key, for React and for adopting the ids a save returns
 * (adoptParticipantIds). CLIENT ONLY: never sent, never stored, never read
 * back from a server.
 */
export const CLIENT_ROW_KEY = "_key";

/**
 * Replace (P4a, rider-rename.ts): the saved rider this draft row is typed in
 * place of — their id. Never an answer, never stored. On a draft row it is
 * the page's note. A WHOLE-LIST save turns it into a tombstone for that id
 * ({_pid, _remove: true}) beside the new row, which goes WITHOUT an id (a
 * new rider, nothing inherited). The ONE-RIDER writer takes it on the wire
 * (20261002100020, review round): {_replaces: <id>, ...the new rider's
 * answers} is Replace in ONE save (replaceSaveRow), so a place is never left
 * for nobody between two calls. questionsPayload never sends it.
 */
export const REPLACES_KEY = "_replaces";

/**
 * "Yes, fixing a spelling" (staff and above, P4a): the name answers the Yes
 * was given for, {key: identityValue}. CLIENT ONLY: never sent. A name typed
 * after the Yes is not what was confirmed, so the Yes no longer counts
 * (samePersonHolds): questionsPayload drops "_same_person" and the question
 * is asked again (review round: a Yes for "Chris" must not carry Kris's
 * answers onto "Bob Smith").
 */
export const SAME_NAME_KEY = "_same_name";

/**
 * Undo on a replaced card (P4a): the card as it was when Replace was chosen,
 * with the saved rider's name put back and every other edit kept. CLIENT
 * ONLY: never sent.
 */
export const UNDO_ROW_KEY = "_undo";

/** One of the reserved keys a save may carry (not the client-only _key). */
export function isReservedRowKey(key: string): key is ReservedRowKey {
  return (RESERVED_ROW_KEYS as readonly string[]).includes(key);
}

/** A reserved key or a client-only key (_key, _replaces, _same_name, _undo): never an answer. */
export function isRowMarkerKey(key: string): boolean {
  return (
    key === CLIENT_ROW_KEY || key === REPLACES_KEY || key === SAME_NAME_KEY || key === UNDO_ROW_KEY || isReservedRowKey(key)
  );
}

/**
 * An answer as the name rule compares it (the server's
 * _participant_identity_value): whitespace runs collapsed, trimmed, NFC,
 * lower case; a list sorted and joined; nothing is "". (The database's
 * lower() may differ from toLowerCase() on a rare letter: the server's
 * verdict wins either way — a refusal opens the question.)
 */
export function identityValue(v: unknown): string {
  if (v === undefined || v === null) return "";
  const text = (s: string) => s.replace(/[ \t\n\r\f\v]+/g, " ").trim().normalize("NFC").toLowerCase();
  if (typeof v === "string") return text(v);
  if (Array.isArray(v))
    return v
      .map((x) => text(typeof x === "string" ? x : JSON.stringify(x)))
      .sort()
      .join("\u001f");
  return JSON.stringify(v);
}

/**
 * Does this row's "same person" still stand? Only while every name answer it
 * was given for (SAME_NAME_KEY) is unchanged. It fails closed (review round
 * 2): a row that says "_same_person" with no record of the name it was given
 * for (built by a caller, not the prompt's confirmSamePerson) is not a Yes,
 * so it is never sent and the question is asked.
 */
export function samePersonHolds(row: unknown): boolean {
  if (!isRecord(row) || row[SAME_PERSON_KEY] !== true) return false;
  const was = row[SAME_NAME_KEY];
  if (!isRecord(was)) return false;
  return Object.entries(was).every(([key, value]) => identityValue(row[key]) === value);
}

// ─── ids ────────────────────────────────────────────────────────────────────
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A uuid string (the server stores them lower case; it accepts either). */
export function isParticipantId(v: unknown): v is string {
  return typeof v === "string" && UUID_RE.test(v);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** The row's id, lower case, or null (a new person, or a server that sends none). */
export function participantIdOf(row: unknown): string | null {
  if (!isRecord(row)) return null;
  const id = row[PARTICIPANT_ID_KEY];
  return isParticipantId(id) ? id.toLowerCase() : null;
}

/** The row's client-only key, or null. */
export function rowKeyOf(row: unknown): string | null {
  if (!isRecord(row)) return null;
  const key = row[CLIENT_ROW_KEY];
  return typeof key === "string" && key !== "" ? key : null;
}

/** A tombstone ({_pid, _remove: true}): removes a saved person; not a person of the save. */
export function isTombstone(row: unknown): boolean {
  return isRecord(row) && Object.prototype.hasOwnProperty.call(row, REMOVE_KEY);
}

/** The row's answers alone: every reserved key and the client-only _key taken out. */
export function rowAnswers<R extends Record<string, unknown>>(row: R | null | undefined): Partial<R> {
  const out: Record<string, unknown> = {};
  if (!isRecord(row)) return out as Partial<R>;
  for (const [k, v] of Object.entries(row)) if (!isRowMarkerKey(k)) out[k] = v;
  return out as Partial<R>;
}

/**
 * A row with no answers at all: {} or a placeholder {_pid} (a person whose
 * details are not in yet). "Empty" is judged on the answers only, never on
 * the id: (row - '_pid') = '{}' on the server.
 */
export function isPlaceholderRow(row: unknown): boolean {
  if (!isRecord(row)) return false;
  return Object.keys(row).every(isRowMarkerKey);
}

// ─── a save's reply ─────────────────────────────────────────────────────────
/**
 * The participant_ids of a save's reply: the stored rows' ids, in stored
 * order (the rows sent, tombstones skipped). null when the reply has none
 * (today's server) or they are not all ids: then nothing is adopted.
 */
export function participantIdsOf(reply: unknown): string[] | null {
  const ids = isRecord(reply) ? reply.participant_ids : undefined;
  if (!Array.isArray(ids) || !ids.every(isParticipantId)) return null;
  return ids.map((id) => id.toLowerCase());
}

/** The booker's own row id on a form or a save's reply (guest profiles), or null. */
export function bookerParticipantIdOf(v: unknown): string | null {
  const id = isRecord(v) ? v.booker_participant_id : undefined;
  return isParticipantId(id) ? id.toLowerCase() : null;
}

/**
 * The client-only keys of the rows a save sends, in the order the server
 * numbers them (tombstones skipped): recorded at SEND time, so the ids the
 * reply returns land on the rows that were sent, whatever the draft looks
 * like by then. null for a row that has no key (its id is not adopted).
 */
export function sentRowKeys(rows: readonly unknown[]): (string | null)[] {
  return rows.filter((row) => !isTombstone(row)).map(rowKeyOf);
}

/**
 * Write the ids a save returned onto the draft rows that were sent: the i-th
 * id goes to the row whose client-only key is sentKeys[i] — found by that
 * key, NEVER by where the row sits in the draft now. Nothing is adopted
 * when the reply has no ids (today's server) or a different number of them
 * than rows were sent. Returns `rows` itself when nothing changes.
 */
export function adoptParticipantIds<R extends Record<string, unknown>>(
  rows: readonly R[],
  sentKeys: readonly (string | null)[],
  ids: readonly string[] | null,
): R[] {
  if (!ids || ids.length !== sentKeys.length) return rows as R[];
  const idByKey = new Map<string, string>();
  sentKeys.forEach((key, i) => {
    if (key) idByKey.set(key, ids[i]!);
  });
  let out: R[] | null = null;
  rows.forEach((row, i) => {
    const key = rowKeyOf(row);
    const id = key ? idByKey.get(key) : undefined;
    if (!id || participantIdOf(row) === id) return;
    out ??= [...rows];
    // A replaced card saved (P4a): it is its own rider now, under its own id
    // (and the saved rider's card kept for Undo goes with the note).
    const { [REPLACES_KEY]: _was, [UNDO_ROW_KEY]: _undo, ...rest } = row as Record<string, unknown>;
    out[i] = { ...rest, [PARTICIPANT_ID_KEY]: id } as unknown as R;
  });
  return out ?? (rows as R[]);
}

/**
 * What the server now holds after a save it accepted: the rows sent
 * (tombstones skipped), each with the id the reply gave it. Without ids in
 * the reply: the rows sent, with consumed self intent removed.
 */
export function withReturnedIds<R extends Record<string, unknown>>(sent: readonly R[], ids: readonly string[] | null): R[] {
  // The server consumes _self into the binding; it is never stored as an
  // answer, including when an older reply does not carry participant ids.
  const stored = sent.some((row) => SELF_KEY in row) ? sent.map((row) => {
    if (!(SELF_KEY in row)) return row;
    const { [SELF_KEY]: _self, ...answers } = row;
    return answers as R;
  }) : sent;
  const kept = stored.filter((row) => !isTombstone(row));
  if (!ids || ids.length !== kept.length) return stored as R[];
  return kept.map((row, i) => ({ ...row, [PARTICIPANT_ID_KEY]: ids[i]! }) as R);
}

/**
 * Do the draft rows still belong to the people the server holds at the same
 * places? Each row's id must equal the stored row's id at its index (both
 * none counts as equal: a new person, or a server without ids). A draft row
 * without an id over a stored PLACEHOLDER (an id, no answer: a rider not in
 * yet, which the one-rider writer pads the party with) is the same too:
 * nobody is there to be someone else. A draft whose rows no longer match is
 * not kept (mergeDraft): another tab, the venue or the guest's own link
 * changed who is on the booking.
 */
export function sameParticipants(draft: readonly unknown[], stored: readonly unknown[]): boolean {
  return draft.every((row, i) => {
    const id = participantIdOf(row);
    if (id === null && isPlaceholderRow(stored[i])) return true;
    // A new rider typed in place of this very saved rider (Replace, not yet
    // saved): still the page's own edit of the same place.
    if (id === null && replacedIdOf(row) !== null) return replacedIdOf(row) === participantIdOf(stored[i]);
    return id === participantIdOf(stored[i]);
  });
}

/** The saved rider a draft row replaces (REPLACES_KEY, lower case), or null. */
export function replacedIdOf(row: unknown): string | null {
  if (!isRecord(row)) return null;
  const id = row[REPLACES_KEY];
  return isParticipantId(id) ? id.toLowerCase() : null;
}

/**
 * The draft rows a merge keeps (sameParticipants), each row without an id
 * given the id of the stored placeholder at its place: the slot the guest is
 * filling in, which holds no answer, so nothing is inherited. Every save of
 * the whole list must then name that slot (the server refuses a stored id
 * left out). A row with an id, or over a stored rider with answers, is
 * untouched. Returns `rows` itself when nothing changes.
 */
export function withPlaceholderIds<R extends Record<string, unknown>>(rows: readonly R[], stored: readonly unknown[]): R[] {
  let out: R[] | null = null;
  rows.forEach((row, i) => {
    if (participantIdOf(row) !== null || !isPlaceholderRow(stored[i])) return;
    const id = participantIdOf(stored[i]);
    if (!id) return;
    out ??= [...rows];
    // A replaced card over the empty place its Replace left (P4a): the saved
    // rider is gone already, so the card is simply the new rider there.
    const { [REPLACES_KEY]: _was, [UNDO_ROW_KEY]: _undo, ...rest } = row as Record<string, unknown>;
    out[i] = { ...rest, [PARTICIPANT_ID_KEY]: id } as unknown as R;
  });
  return out ?? (rows as R[]);
}

/** A one-rider save's participant_id (lower case), or null (a server before P3a, or not an id). */
export function participantIdOfReply(reply: unknown): string | null {
  const id = isRecord(reply) ? reply.participant_id : undefined;
  return isParticipantId(id) ? id.toLowerCase() : null;
}

// ─── refusals the server change adds ────────────────────────────────────────
/**
 * The ids in a save no longer match the people the server holds (an id
 * missing, unknown, repeated or moved). The client cannot mend its draft:
 * it reads the forms again and says so (PARTICIPANTS_CHANGED_NOTICE).
 */
export const PARTICIPANT_ID_REFUSALS = [
  "participant_missing",
  "participant_unknown",
  "participant_order_changed",
  "participant_duplicate",
] as const;

export function isParticipantIdRefusal(reason: unknown): boolean {
  return typeof reason === "string" && (PARTICIPANT_ID_REFUSALS as readonly string[]).includes(reason);
}

/** Shown with the forms read again after one of PARTICIPANT_ID_REFUSALS, or a draft that no longer matched. */
export const PARTICIPANTS_CHANGED_NOTICE = "These details were changed elsewhere, please check them.";

function title(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function nounOf(noun: GuestNoun | null | undefined): GuestNoun {
  return noun && noun.one && noun.other ? noun : { one: "guest", other: "guests" };
}

/** "Rider 2", "Riders 1 and 3", "Riders 1, 2 and 4". */
function numbered(noun: GuestNoun, numbers: readonly number[]): string {
  const n = [...new Set(numbers)].sort((a, b) => a - b);
  if (n.length === 1) return `${title(noun.one)} ${n[0]}`;
  return `${title(noun.other)} ${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
}

/**
 * identity_changed: a saved person's name was changed, and nothing said it
 * is still the same person. Each person's private answers stay with the
 * person they were given for, so the server will not take a new name for
 * them without that answer. The surfaces ask it on the card ("Is this still
 * {name}?", rider-rename.ts, P4a); these words go with the refusal that
 * opens the question. `numbers` are the people's numbers on the form
 * (1-based), from the ids the refusal names; none known: said in general.
 * `staff`: the venue's own editor.
 */
export function identityChangedMessage(
  noun: GuestNoun | null | undefined,
  numbers: readonly number[] = [],
  opts: { staff?: boolean } = {},
): string {
  const g = nounOf(noun);
  const many = new Set(numbers).size > 1;
  const who = numbers.length === 0 ? `a ${g.one}'s name` : many ? `the names of ${numbered(g, numbers)}` : `${numbered(g, numbers)}'s name`;
  const same = many ? "whether they are still the same people" : "whether it is still the same person";
  return opts.staff
    ? `Not saved: you changed ${who} after their details were saved. Say ${same} (on the card) and save again.`
    : `You changed ${who} after their details were saved. Please tell us ${same}.`;
}

/**
 * participant_missing that names only people the server holds BEYOND the
 * party (the party went down under saved people): not a page out of date,
 * so reading the forms again cannot mend it. The server drops the empty
 * places past the party by itself; a saved person is never dropped without
 * being asked about ("Who is not coming?", P4). `stored`: every row the
 * reader returned, in the server's order.
 */
export function missingBeyondParty(
  reason: unknown,
  stored: readonly unknown[],
  partySize: number,
  ids: readonly string[] | null,
): boolean {
  if (reason !== "participant_missing" || !ids || ids.length === 0) return false;
  return ids.every((id) => {
    const at = stored.findIndex((row) => participantIdOf(row) === id.toLowerCase());
    return at >= 0 && at >= partySize;
  });
}

/**
 * The words for missingBeyondParty: how many people are saved, and what the
 * booking is for now. `staff`: the venue's own editor.
 */
export function ridersBeyondPartyMessage(
  noun: GuestNoun | null | undefined,
  saved: number,
  party: number,
  opts: { staff?: boolean } = {},
): string {
  const g = nounOf(noun);
  const head = `${saved} ${saved === 1 ? g.one : g.other} ${saved === 1 ? "is" : "are"} saved on this booking, but it is now for ${party}.`;
  // A saved person is never removed without asking: the surfaces ask "Who
  // is not coming?" (rider-rename.ts, P4a).
  return opts.staff
    ? `Not saved: ${head} Choose who is not coming first, or set the number back to ${saved}.`
    : `${head} Please choose who is not coming, or change the number back to ${saved}.`;
}

/**
 * The refusals the server change (and guest profiles) add, in words, or
 * null for any other reason (the surface keeps its own words for those).
 * `numbers`: the people the refusal names, when the surface can tell.
 * `staff`: the venue's own editor.
 */
export function describeParticipantRefusal(
  reason: unknown,
  noun: GuestNoun | null | undefined,
  numbers: readonly number[] = [],
  opts: { staff?: boolean } = {},
): string | null {
  const g = nounOf(noun);
  if (isParticipantIdRefusal(reason)) return PARTICIPANTS_CHANGED_NOTICE;
  switch (reason) {
    case "identity_changed":
      return identityChangedMessage(g, numbers, opts);
    case "sensitive_answers_required":
      return numbers.length
        ? `Please answer the private questions for ${numbered(g, numbers)} again.`
        : `Please answer the private questions for each ${g.one} again.`;
    case "participant_self_twice":
      return `Only one ${g.one} on this booking can be you. Please check the details.`;
    case "participant_already_in_visit":
      return `That person is already one of the ${g.other} on this booking. Please check the details.`;
    case "participant_pick_not_allowed":
      return "That person could not be added from your saved people. Please enter their details instead.";
    case "participant_pick_unknown":
      return "That person could not be found. Please enter their details instead.";
    // A saved person is chosen only where the booking's own questions say
    // which one holds a person's name (20261002090010, review round 2): the
    // booking keeps the questions it was made with, so marking the name now
    // helps the bookings made after it.
    case "participant_pick_needs_name_question":
      return opts.staff
        ? `Not saved: a saved person can only be chosen when the ${g.one} questions say which one holds the ${g.one}'s name, and this booking's questions don't. Mark the name question in Booking questions first (bookings made after that can choose saved people). For this booking, enter their details instead.`
        : "That person could not be added from your saved people. Please enter their details instead.";
    // "This is me" is the booker, an existing person too: taken only where
    // the booking's own questions say which one holds a person's name
    // (20261002090010, review round 3).
    case "participant_self_needs_name_question":
      return opts.staff
        ? `Not saved: the booker can only be marked as one of the ${g.other} when the ${g.one} questions say which one holds the ${g.one}'s name, and this booking's questions don't. Mark the name question in Booking questions first (bookings made after that can mark the booker). For this booking, enter their details instead.`
        : `This booking can't record which ${g.one} is you. Please enter your details as one of the ${g.other} instead.`;
    case "participants_unbound":
      return `${title(g.one)} details could not be saved. Please try again.`;
    default:
      return null;
  }
}

/**
 * The people a refusal names, as numbers on the form: each id in the
 * refusal's participant_ids is looked up among `rows` (the rows as the
 * surface shows them) by id, never by position; `first` is the number of
 * rows[0] (1 for a form's first person). Ids not found are left out.
 */
export function numbersOfIds(rows: readonly unknown[], ids: readonly string[] | null, first = 1): number[] {
  if (!ids) return [];
  const out: number[] = [];
  for (const id of ids) {
    const at = rows.findIndex((row) => participantIdOf(row) === id.toLowerCase());
    if (at >= 0) out.push(first + at);
  }
  return out;
}
